import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { Box, Divider, makeStyles, Tab, Typography } from '@material-ui/core';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import NoPaperPageContent, { NoPaperPageButton } from 'components/Generic/NoPaperPageContent';
import { useExpeditionById } from 'hooks';
import Loading from 'components/Loading';
import { checkDateUpperTimeInterval, createUTCDateFromDateString, formatDateTimeForDisplay } from 'utils/date';
import { Routes } from 'routes';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { useLatestClosureDate } from 'utils/closureHooks';
import { deleteExpedition, exportConfirmationShippingSlip, exportShippingSlip } from 'services/apiOperations';
import { AppContext } from 'context';
import { notify } from 'components/Notifier';
import { goBack } from 'utils/history';
import { floatWeightToInt, formatTonsFromKg } from 'utils/weight';
import ExpeditionDetailsTab from './ExpeditionDetailsTab';
import ExpeditionBuyerConfirmationTab from './ExpeditionBuyerConfirmationTab';
import { UserRole } from '../../types';

const useStyles = makeStyles({
  marginTopDivider: {
    marginTop: '3em',
  },
  label: {
    fontWeight: 600,
    marginRight: 16,

    '&:after': {
      content: '" :"',
    },
  },
  labelWarning: {
    color: '#FF9500',
  },
});

const ExpeditionShow: React.FC = () => {
  const TAB_ID = {
    EXPEDITION: 'EXPEDITION',
    BUYER_CONFIRMATION: 'BUYER_CONFIRMATION',
  };
  const classes = useStyles();
  const history = useHistory();

  const [customButtons, setCustomButtons] = useState<NoPaperPageButton[]>([]);

  const { expeditionId } = useParams<{ expeditionId: string }>();

  const { t } = useTranslation();
  const { value: expedition, loading } = useExpeditionById(expeditionId);

  useEffect(() => {
    if (
      expedition?.expeditionBuyerConfirmation &&
      (expedition.expeditionConfirmedClientPortions.length > 0 || expedition.expeditionClientPortions.length > 0)
    ) {
      setCustomButtons([
        {
          text: t('expeditions.export.shippingSlip'),
          action: () => {
            exportShippingSlip(expeditionId, t);
          },
        },
        {
          text: t('expeditions.export.confirmationShippingSlip'),
          action: () => {
            exportConfirmationShippingSlip(expeditionId, t);
          },
        },
      ]);
      return;
    }
    setCustomButtons([
      {
        text: t('expeditions.export.shippingSlip'),
        action: () => {
          exportShippingSlip(expeditionId, t);
        },
      },
    ]);
  }, [expedition, setCustomButtons, expeditionId, t]);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const roles = context.getRoles?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const expeditionDate = expedition ? createUTCDateFromDateString(expedition.expeditionDate) : new Date();
  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));
  const canDelete = !readOnly && (expedition ? !checkDateUpperTimeInterval(expeditionDate, closureMonth) : false);
  const canEdit = canDelete && !expedition?.isMiseEnCoherenceAccepted;

  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TAB_ID.EXPEDITION);
  const handleChangeTab = (_event: any, newValue: string) => {
    setSelectedTab(newValue);
  };
  const [currentTonnageAccepteInTons, setCurrentTonnageAccepteInTons] = useState(0);
  const [currentTonnageDecoteInTons, setCurrentTonnageDecoteInTons] = useState(0);
  const [tonnageReceveid, setTonnageReceveid] = useState(0);

  useEffect(() => {
    const totalTonnage = floatWeightToInt(currentTonnageDecoteInTons) + floatWeightToInt(currentTonnageAccepteInTons);
    setTonnageReceveid(
      totalTonnage > 0 && currentTonnageAccepteInTons > 0
        ? totalTonnage
        : expedition?.expeditionBuyerConfirmation?.tonnageRecu || 0,
    );
  }, [currentTonnageDecoteInTons, currentTonnageAccepteInTons, expedition]);

  const labelWarning =
    (Number(expedition?.weight) < tonnageReceveid && currentTonnageAccepteInTons) > 0 ? classes.labelWarning : '';

  return (
    <>
      <NoPaperPageContent
        title={t('expeditions.show.title')}
        backAction={() => goBack(Routes.Expeditions, history)}
        updateButtonLink={
          canEdit
            ? {
                pathname: generatePath(Routes.ExpeditionEdit, { expeditionId }),
                state: { referer: generatePath(Routes.ExpeditionShow, { expeditionId }) },
              }
            : undefined
        }
        deleteButtonAction={canDelete ? () => setConfirmDeleteModalOpen(true) : undefined}
        customButtons={customButtons}
      >
        {loading ? (
          <Loading />
        ) : (
          expedition && (
            <Box maxWidth="80%">
              <DeleteConfirmationDialog
                title={t('expeditionCreateForm.deletion.confirm')}
                isOpen={confirmDeleteModalOpen}
                setOpen={open => setConfirmDeleteModalOpen(open)}
                confirmDeletion={async () => {
                  setConfirmDeleteModalOpen(false);

                  await deleteExpedition(expeditionId);
                  notify({
                    message: t('expeditionCreateForm.deletion.success'),
                    severity: 'success',
                  });
                  goBack(Routes.Expeditions, history);
                }}
              />
              <Divider className={classes.marginTopDivider} />
              <Box display="flex" paddingTop="26px" paddingBottom="26px">
                <Box marginRight="200px">
                  <Typography>
                    <span className={classes.label}>{t('expeditionCreateForm.material')}</span>
                    {expedition.material.shortName}
                  </Typography>
                </Box>
                <Box marginRight="200px">
                  <Typography>
                    <span className={classes.label}>{t('expeditionCreateForm.buyer')}</span>
                    {expedition.buyer.name}
                  </Typography>
                </Box>
                <Box marginRight="200px">
                  <Typography>
                    <span className={classes.label}>{t('expeditionCreateForm.dateTime')}</span>
                    {formatDateTimeForDisplay(expedition.expeditionDate)}
                  </Typography>
                </Box>
              </Box>
              <Divider />
              <Box display="flex" paddingTop="26px" paddingBottom="52px">
                <Box marginRight="8.5%">
                  <Typography>
                    <span className={classes.label}>{t('expeditionCreateForm.weight')}</span>
                    {formatTonsFromKg(expedition.weight)}
                  </Typography>
                </Box>
                <Typography>
                  <span className={classes.label}>{t('expeditionCreateForm.weightReceived')}</span>
                  <span className={labelWarning}>{tonnageReceveid > 0 ? formatTonsFromKg(tonnageReceveid) : '-'}</span>
                </Typography>
              </Box>
              <TabContext value={selectedTab}>
                <TabList value={selectedTab} onChange={handleChangeTab} indicatorColor="primary" textColor="primary">
                  <Tab label={t('expeditions.show.tabs.expedition')} value={TAB_ID.EXPEDITION} />
                  <Tab label={t('expeditions.show.tabs.buyerConfirmation')} value={TAB_ID.BUYER_CONFIRMATION} />
                </TabList>
                <Divider />
                <TabPanel value={TAB_ID.EXPEDITION}>
                  <ExpeditionDetailsTab expedition={expedition} />
                </TabPanel>
                <TabPanel value={TAB_ID.BUYER_CONFIRMATION}>
                  <ExpeditionBuyerConfirmationTab
                    expedition={expedition}
                    setCurrentTonnageAccepteInTons={setCurrentTonnageAccepteInTons}
                    setCurrentTonnageDecoteInTons={setCurrentTonnageDecoteInTons}
                    currentTonnageAccepteInTons={currentTonnageAccepteInTons}
                    currentTonnageDecoteInTons={currentTonnageDecoteInTons}
                  />
                </TabPanel>
              </TabContext>
            </Box>
          )
        )}
      </NoPaperPageContent>
    </>
  );
};
export default ExpeditionShow;
